import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiMonitor, FiSmartphone, FiCpu, FiGlobe, FiZap } from 'react-icons/fi';

export default function PlayerCard({ user, highlightGame }) {
  const navigate = useNavigate();
  
  // Filtra i giochi da mostrare: se c'è un filtro specifico mostra solo quello, 
  // altrimenti mostrali tutti (limitati a 3 per mantenere la card leggibile)
  const gamesToDisplay = highlightGame && highlightGame !== 'All'
    ? user.games.filter(g => g.name === highlightGame)
    : user.games.slice(0, 3);

  const getPlatformIcon = (platform) => {
    if (platform === 'PC') return FiCpu;
    if (platform === 'Console') return FiMonitor;
    return FiSmartphone;
  };

  const getRankColor = (rank) => {
    const colors = {
      'Bronze': 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200/50',
      'Silver': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200/50',
      'Gold': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border-yellow-200/50',
      'Platinum': 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300 border-cyan-200/50',
      'Diamond': 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200/50',
      'Master': 'bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200/50',
      'Global Elite': 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200/50',
      'Radiant': 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200/50',
      'Immortal': 'bg-rose-600 text-white border-rose-400'
    };
    return colors[rank] || 'bg-gray-50 text-gray-400 dark:bg-dark-border dark:text-gray-500 border-transparent';
  };

  return (
    <motion.div 
      whileHover={{ y: -4, scale: 1.01 }}
      className={`bg-white dark:bg-dark-surface rounded-[32px] border cursor-pointer transition-all overflow-hidden flex flex-col group relative ${
        user.isOnline 
          ? 'border-brand-200 dark:border-brand-900/50 shadow-md' 
          : 'border-gray-100 dark:border-dark-border shadow-sm hover:shadow-xl hover:shadow-brand-900/5'
      }`}
      onClick={() => navigate(`/profile/${user.id}`)}
    >
      <div className="p-4 flex flex-col h-full">
        {/* User Header Section */}
        <div className="flex gap-3 mb-4">
          <div className="relative shrink-0">
            <img 
              src={user.avatar} 
              alt={user.nickname} 
              className="w-12 h-12 rounded-2xl object-cover border-2 border-white dark:border-dark-surface shadow-sm" 
            />
            <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 border-3 border-white dark:border-dark-surface rounded-full ${
              user.isOnline ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}></span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-black text-brand-950 dark:text-white truncate text-lg leading-tight group-hover:text-brand-600 transition-colors">
              {user.nickname}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5 opacity-60">
              <SafeIcon icon={FiGlobe} className="w-2.5 h-2.5 text-gray-400" />
              <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-dark-muted">
                {(user.languages || []).slice(0, 2).join(', ')}
              </span>
            </div>
          </div>
        </div>

        {/* Games List Section */}
        <div className="space-y-2 flex-1">
          {gamesToDisplay.map((game, idx) => (
            <div 
              key={idx} 
              className="bg-brand-50/40 dark:bg-dark-card/50 rounded-2xl p-3 border border-brand-100/30 dark:border-dark-border/50 group-hover:bg-brand-50 dark:group-hover:bg-dark-border/40 transition-colors duration-300"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-black text-brand-900 dark:text-brand-200 text-[10px] uppercase tracking-tight truncate">
                    {game.name}
                  </span>
                  {game.lookingForTeam && (
                    <span className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 text-[8px] font-black uppercase tracking-tighter border border-amber-500/20">
                      <SafeIcon icon={FiZap} className="w-2 h-2 animate-pulse" />
                      LFG
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <SafeIcon icon={getPlatformIcon(game.platform)} className="w-3 h-3 text-gray-400/70" />
                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded shadow-sm border ${getRankColor(game.rank)}`}>
                    {game.rank || '-'}
                  </span>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center justify-between gap-1">
                <div className="flex flex-wrap gap-1">
                  {(game.roles || []).slice(0, 2).map((role, rIdx) => (
                    <span key={rIdx} className="text-[8px] bg-white dark:bg-dark-surface text-brand-700 dark:text-brand-300 px-1.5 py-0.5 rounded-md font-bold border border-brand-100 dark:border-dark-border">
                      {role}
                    </span>
                  ))}
                </div>
                <span className={`text-[8px] font-black uppercase tracking-widest opacity-40 ${
                  game.type === 'Competitive' ? 'text-red-500' : 'text-emerald-500'
                }`}>
                  {game.type}
                </span>
              </div>
            </div>
          ))}
          
          {/* Supporto visivo se ci sono più giochi di quelli mostrati */}
          {user.games.length > gamesToDisplay.length && (
            <div className="text-center pt-1">
              <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">
                + {user.games.length - gamesToDisplay.length} other games
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}